/**
 * ロギングサービス
 * #2133: darshana の src/lib/logger.ts と同じ方針。本番ではコンソール出力を
 * 抑制し、将来の外部監視（Sentry 等）統合のための差し替えポイントを残す。
 */

interface LogContext {
  [key: string]: unknown;
}

interface Logger {
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, errorOrContext?: Error | unknown | LogContext, context?: LogContext) => void;
  debug: (message: string, context?: LogContext) => void;
}

const formatError = (error: Error | unknown): string => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
};

const isDevOrTest = (): boolean => {
  const mode = import.meta.env.MODE;
  return mode === 'development' || mode === 'test';
};

export const logger: Logger = {
  info: (message, context) => {
    if (!isDevOrTest()) return;
    if (context) {
      console.info(`[INFO] ${message}`, context);
    } else {
      console.info(`[INFO] ${message}`);
    }
  },

  warn: (message, context) => {
    if (!isDevOrTest()) return;
    if (context) {
      console.warn(`[WARN] ${message}`, context);
    } else {
      console.warn(`[WARN] ${message}`);
    }
  },

  error: (message, errorOrContext, context) => {
    if (!isDevOrTest()) return;

    let actualError: Error | unknown | undefined;
    let actualContext: LogContext | undefined;

    if (errorOrContext instanceof Error) {
      actualError = errorOrContext;
      actualContext = context;
    } else if (
      errorOrContext &&
      typeof errorOrContext === 'object' &&
      'name' in errorOrContext &&
      'message' in errorOrContext
    ) {
      actualError = errorOrContext;
      actualContext = context;
    } else if (errorOrContext !== undefined && typeof errorOrContext !== 'object') {
      actualError = errorOrContext;
      actualContext = context;
    } else {
      actualContext = errorOrContext as LogContext | undefined;
    }

    const errorStr = actualError ? formatError(actualError) : '';
    const fullMessage = errorStr ? `${message} - ${errorStr}` : message;

    if (actualContext) {
      console.error(`[ERROR] ${fullMessage}`, actualContext);
    } else {
      console.error(`[ERROR] ${fullMessage}`);
    }
  },

  debug: (message, context) => {
    if (!isDevOrTest()) return;
    if (context) {
      console.log(`[DEBUG] ${message}`, context);
    } else {
      console.log(`[DEBUG] ${message}`);
    }
  },
};

export default logger;
